import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { loginUser, registerUser, loginWithGoogle, resetPassword } from '@3dera/core/services/auth';
import { isAgeValid, getMaxBirthDate, getMinBirthDate } from '@3dera/core/utils/dateUtils';
import { useLanguage } from '../../context/LanguageContext';
import FormField from '../../components/FormField/FormField';
import PremiumButton from '../../components/PremiumButton/PremiumButton';
import AlertBanner from '../../components/AlertBanner/AlertBanner';
import styles from './Auth.module.css';

// Şifre göster/gizle göz ikonları
const EyeIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L9.88 9.88" />
  </svg>
);

const Auth = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    dateOfBirth: ''
  });
  const [rememberMe, setRememberMe] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState({ type: '', text: '' });
  const navigate = useNavigate();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;
    setResetLoading(true);
    setResetMessage({ type: '', text: '' });
    const result = await resetPassword(resetEmail.trim());
    if (result.success) {
      setResetMessage({ type: 'success', text: 'Şifre sıfırlama bağlantısı e-posta adresinize gönderildi. Lütfen gelen kutunuzu kontrol edin.' });
    } else {
      setResetMessage({ type: 'error', text: result.error || 'Bir hata oluştu. Lütfen tekrar deneyin.' });
    }
    setResetLoading(false);
  };

  useEffect(() => {
    document.title = `3DEra - ${t('auth.signIn')}`;
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem('account_deactivated_message')) {
        setError(t('auth.accountDisabled'));
        sessionStorage.removeItem('account_deactivated_message');
      }
    } catch (e) {}
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Ad ve Soyad için sadece harfler (Türkçe dahil)
    if ((name === 'firstName' || name === 'lastName') && value.length > 0) {
      const cleaned = value.replace(/[^a-zA-ZğĞıİöÖüÜşŞçÇ\s]/g, '');
      if (cleaned.length > 50) {
        setError(name === 'firstName' ? t('auth.firstNameMax') : t('auth.lastNameMax'));
        return;
      }
      // Rakam veya özel karakter girilmeye çalışıldıysa uyar
      if (value !== cleaned && value.length > 0) {
        setError(t('auth.onlyLetters'));
      }
      setFormData(prev => ({
        ...prev,
        [name]: cleaned
      }));
      return;
    }
    
    // Email için max 100 karakter
    if (name === 'email' && value.length > 100) {
      setError(t('auth.emailMax'));
      return;
    }
    
    // Parola için max 50 karakter
    if ((name === 'password' || name === 'confirmPassword') && value.length > 50) {
      setError(t('auth.passwordMax'));
      return;
    }

    // Parola güvenlik kontrolü (kayıt sekmesinde)
    if (name === 'password' && activeTab === 'signup') {
      setPasswordStrength({
        hasMinLength: value.length >= 6,
        hasUpperCase: /[A-Z]/.test(value),
        hasNumber: /[0-9]/.test(value),
        hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)
      });
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Hata mesajını 3 saniye sonra otomatik kapat
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleGoogleSignIn = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await loginWithGoogle(rememberMe);
      
      if (result.success) {
        // console.log('Google ile giriş başarılı:', result.user);
        setSuccess(t('auth.loginSuccess'));

        // 1.5 saniye sonra yönlendir
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(t('auth.googleError'));
      // console.error('Google auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (activeTab === 'login') {
        // GİRİŞ İŞLEMİ
        const result = await loginUser(formData.email, formData.password, rememberMe);
        
        if (result.success) {
          // console.log('Giriş başarılı:', result.user);
          setSuccess(t('auth.loginSuccess'));

          // 1.5 saniye sonra yönlendir
          setTimeout(() => {
            navigate('/');
          }, 1500);
        } else {
          setError(result.error);
        }
      } else {
        // KAYIT İŞLEMİ
        // Ad Soyad kontrolü
        if (!formData.firstName.trim() || formData.firstName.trim().length < 2) {
          setError(t('auth.firstNameMin'));
          setLoading(false);
          return;
        }
        
        if (!formData.lastName.trim() || formData.lastName.trim().length < 2) {
          setError(t('auth.lastNameMin'));
          setLoading(false);
          return;
        }
        
        // Email kontrolü
        if (!formData.email || formData.email.trim().length === 0) {
          setError(t('auth.enterEmail'));
          setLoading(false);
          return;
        }

        // Parola kontrolü
        if (formData.password !== formData.confirmPassword) {
          setError(t('auth.passwordsMismatch'));
          setLoading(false);
          return;
        }

        // Parola uzunluğu kontrolü
        if (formData.password.length < 6) {
          setError(t('auth.passwordMin'));
          setLoading(false);
          return;
        }
        
        if (formData.password.length > 50) {
          setError(t('auth.passwordMax'));
          setLoading(false);
          return;
        }

        // Parola güvenlik gereksinimleri kontrolü
        const hasUpperCase = /[A-Z]/.test(formData.password);
        const hasLowerCase = /[a-z]/.test(formData.password);
        const hasNumber = /[0-9]/.test(formData.password);
        const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password);
        
        if (!hasUpperCase) {
          setError(t('auth.uppercaseReq'));
          setLoading(false);
          return;
        }
        
        if (!hasNumber) {
          setError(t('auth.numberReq'));
          setLoading(false);
          return;
        }
        
        if (!hasSpecialChar) {
          setError(t('auth.specialReq'));
          setLoading(false);
          return;
        }

        // Yaş kontrolü (18 yaş minimum)
        if (!formData.dateOfBirth) {
          setError(t('auth.enterDob'));
          setLoading(false);
          return;
        }
        
        if (!isAgeValid(formData.dateOfBirth, 18)) {
          setError(t('auth.underAge'));
          setLoading(false);
          return;
        }
        
        // Kullanım Koşulları ve KVKK onayı zorunlu
        if (!termsAccepted || !kvkkAccepted) {
          setError(t('auth.acceptTerms'));
          setLoading(false);
          return;
        }

        const now = new Date().toISOString();
        const userData = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          dateOfBirth: formData.dateOfBirth,
          termsAcceptedAt: now,
          kvkkAcceptedAt: now
        };

        // console.log('🚀 Kayıt işlemi başlatılıyor:', { email: formData.email, userData });
        const result = await registerUser(formData.email, formData.password, userData);
        
        if (result.success) {
          // console.log('✅ Kayıt başarılı:', result.user);
          setSuccess(t('auth.registerSuccess'));
          
          // 1.5 saniye sonra yönlendir
          setTimeout(() => {
            navigate('/');
          }, 1500);
        } else {
          // console.error('❌ Kayıt hatası:', result.error);
          setError(result.error);
        }
      }
    } catch (err) {
      setError(t('auth.unexpectedError'));
      // console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.authContainer}>
      <div className={styles.authBox}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'login' ? styles.active : ''}`}
            onClick={() => setActiveTab('login')}
          >
            {t('auth.signIn')}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'signup' ? styles.active : ''}`}
            onClick={() => setActiveTab('signup')}
          >
            {t('auth.signUp')}
          </button>
        </div>

        {error && (
          <AlertBanner
            severity="error"
            className={styles.authAlert}
            message={error}
            onDismiss={() => setError('')}
          />
        )}

        {success && (
          <AlertBanner
            severity="success"
            className={styles.authAlert}
            message={success}
          />
        )}

        <button 
          type="button"
          onClick={handleGoogleSignIn} 
          className={styles.providerBtn}
          disabled={loading}
        >
          <svg className={styles.providerIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {t('auth.withGoogle')} {activeTab === 'login' ? t('auth.signIn') : t('auth.continue')}
        </button>
        {activeTab === 'login' && (
          <p className={styles.providerHint}>{t('auth.persistNote')}</p>
        )}

        <div className={styles.divider}>
          <span>{t('auth.or')}</span>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {activeTab === 'signup' && (
            <>
              <div className={styles.nameRow}>
                <FormField
                  as="input"
                  type="text"
                  id="firstName"
                  name="firstName"
                  label={t('auth.firstName')}
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                  minLength="2"
                  maxLength="50"
                  pattern="[a-zA-ZğĞıİöÖüÜşŞçÇ\s]+"
                  title={t('auth.onlyLetters')}
                  placeholder="Ahmet"
                />
                <FormField
                  as="input"
                  type="text"
                  id="lastName"
                  name="lastName"
                  label={t('auth.lastName')}
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                  minLength="2"
                  maxLength="50"
                  pattern="[a-zA-ZğĞıİöÖüÜşŞçÇ\s]+"
                  title={t('auth.onlyLetters')}
                  placeholder="Yılmaz"
                />
              </div>

              <FormField
                as="input"
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                label={t('auth.dateOfBirth')}
                required
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                max={getMaxBirthDate(18)}
                min={getMinBirthDate(100)}
              />
            </>
          )}

          <FormField
            as="input"
            type="email"
            id="email"
            name="email"
            label={t('auth.email')}
            required
            value={formData.email}
            onChange={handleInputChange}
            maxLength="100"
            placeholder={t('contact.emailPlaceholder')}
          />

          <div className={styles.formGroup}>
            <FormField
              as="input"
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              label={t('auth.password')}
              required
              value={formData.password}
              onChange={handleInputChange}
              minLength="6"
              maxLength="50"
              placeholder={t('auth.passwordExample')}
              suffix={
                <button
                  type="button"
                  className={styles.eyeToggle}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              }
            />
            {activeTab === 'signup' && formData.password.length > 0 && (
              <div className={styles.passwordRequirements}>
                <span className={passwordStrength.hasMinLength ? styles.reqMet : styles.reqUnmet}>6+</span>
                <span className={passwordStrength.hasUpperCase ? styles.reqMet : styles.reqUnmet}>A-Z</span>
                <span className={passwordStrength.hasNumber ? styles.reqMet : styles.reqUnmet}>0-9</span>
                <span className={passwordStrength.hasSpecialChar ? styles.reqMet : styles.reqUnmet}>!@#</span>
              </div>
            )}
            {activeTab === 'login' && (
              <div className={styles.passwordHelpRow}>
                <small className={styles.helpText}>
                  {t('auth.minChars')}
                </small>
                <button
                  type="button"
                  className={styles.forgotPasswordBtn}
                  onClick={() => { setShowForgotPassword(true); setResetEmail(formData.email); setResetMessage({ type: '', text: '' }); }}
                >
                  Şifremi Unuttum
                </button>
              </div>
            )}
          </div>

          {/* Şifremi Unuttum Modal */}
          {showForgotPassword && (
            <div className={styles.forgotOverlay} onClick={() => setShowForgotPassword(false)}>
              <div className={styles.forgotCard} onClick={(e) => e.stopPropagation()}>
                <h3 className={styles.forgotTitle}>Şifre Sıfırlama</h3>
                <p className={styles.forgotText}>
                  E-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim.
                </p>
                {resetMessage.text && (
                  <div className={`${styles.forgotMessage} ${resetMessage.type === 'success' ? styles.forgotSuccess : styles.forgotError}`}>
                    {resetMessage.text}
                  </div>
                )}
                {resetMessage.type !== 'success' && (
                  <form onSubmit={handleForgotPassword}>
                    <input
                      type="email"
                      className={styles.forgotInput}
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="ornek@email.com"
                      required
                      autoFocus
                    />
                    <button
                      type="submit"
                      className={styles.forgotSubmitBtn}
                      disabled={resetLoading || !resetEmail.trim()}
                    >
                      {resetLoading ? 'Gönderiliyor...' : 'Sıfırlama Bağlantısı Gönder'}
                    </button>
                  </form>
                )}
                <button
                  type="button"
                  className={styles.forgotCloseBtn}
                  onClick={() => setShowForgotPassword(false)}
                >
                  {resetMessage.type === 'success' ? 'Kapat' : 'İptal'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'signup' && (
            <FormField
              as="input"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              label={t('auth.confirmPassword')}
              required
              value={formData.confirmPassword}
              onChange={handleInputChange}
              minLength="6"
              maxLength="50"
              placeholder={t('auth.reenterPassword')}
              error={
                formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword
                  ? t('auth.passwordMismatch')
                  : ''
              }
              suffix={
                <button
                  type="button"
                  className={styles.eyeToggle}
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                  aria-pressed={showConfirmPassword}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              }
            />
          )}

          {activeTab === 'signup' && (
            <div className={styles.legalConsent}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span>
                  <Link to="/kullanim-kosullari" target="_blank" rel="noopener noreferrer">{t('auth.terms')}</Link>'nı
                  {t('auth.termsAccept')}
                </span>
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={kvkkAccepted}
                  onChange={(e) => setKvkkAccepted(e.target.checked)}
                />
                <span>
                  <Link to="/kvkk-aydinlatma" target="_blank" rel="noopener noreferrer">{t('auth.kvkk')}</Link>'ni
                  {t('auth.kvkkAccept')}
                </span>
              </label>
            </div>
          )}

          {activeTab === 'login' && (
            <div className={styles.rememberMeGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>{t('auth.rememberMe')}</span>
              </label>
            </div>
          )}

          <PremiumButton
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={loading}
            disabled={
              loading ||
              (activeTab === 'signup' && (
                !formData.firstName.trim() ||
                !formData.lastName.trim() ||
                !formData.email.trim() ||
                !formData.dateOfBirth ||
                !formData.password ||
                !formData.confirmPassword ||
                formData.password !== formData.confirmPassword ||
                !passwordStrength.hasMinLength ||
                !passwordStrength.hasUpperCase ||
                !passwordStrength.hasNumber ||
                !passwordStrength.hasSpecialChar ||
                !termsAccepted ||
                !kvkkAccepted
              ))
            }
          >
            {loading ? t('auth.processing') : (activeTab === 'login' ? t('auth.signIn') : t('auth.signUp'))}
          </PremiumButton>

        </form>
      </div>
    </div>
  );
};

export default Auth; 